/**
 * 聊天输入框 (报价 / 画图共用): 胶囊输入框 + 圆形绿色发送按钮 (同旧版)。
 * Enter 发送, Shift+Enter 换行 (输入法组字中的 Enter 不算); 1000 字上限 + 计数。
 * 下方一行: 左侧放页面操作 (如「新对话」), 右侧快捷键提示 + 字数。
 */
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { ArrowUp } from "lucide-react";
import { Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";

/** 与旧版输入框一致 */
export const COMPOSER_MAX_LENGTH = 1000;
/** 自动增高的上限 (px), 超出后内部滚动 */
const MAX_HEIGHT = 160;

interface Props {
  pending: boolean;
  onSend: (text: string) => void;
  placeholder?: string;
  /** 受控模式: 父组件持有输入内容 (点示例时填入输入框) */
  value?: string;
  onChange?: (text: string) => void;
  inputRef?: RefObject<HTMLTextAreaElement>;
  /** 输入框下方左侧的操作 (如「新对话」) */
  actions?: ReactNode;
}

export function Composer({
  pending,
  onSend,
  placeholder = "描述产品, 如 KT板 1.2×0.6米 50张",
  value,
  onChange,
  inputRef,
  actions,
}: Props) {
  const [inner, setInner] = useState("");
  const controlled = value !== undefined;
  const text = controlled ? value : inner;
  const setText = (t: string) => {
    if (!controlled) setInner(t);
    onChange?.(t);
  };

  const ownRef = useRef<HTMLTextAreaElement>(null);
  const ref = inputRef ?? ownRef;

  // 随内容自动增高
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [text, ref]);

  const submit = () => {
    const t = text.trim();
    if (!t || pending) return;
    onSend(t);
    setText("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const canSend = !pending && !!text.trim();
  const nearLimit = text.length >= COMPOSER_MAX_LENGTH * 0.9;

  return (
    <div className="shrink-0 bg-gradient-to-t from-background via-background to-background/0 px-3 pb-2 pt-2 md:px-4 md:pb-4">
      <div className="mx-auto max-w-3xl">
        <div
          className={cn(
            "flex items-end gap-2 rounded-[1.75rem] border-2 border-border bg-muted/70 py-1 pl-4 pr-1 transition-colors",
            "focus-within:border-brand focus-within:bg-card focus-within:shadow-[0_0_0_4px_rgba(76,175,80,0.12)]",
            pending && "opacity-80",
          )}
        >
          <textarea
            ref={ref}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-label={placeholder}
            rows={1}
            maxLength={COMPOSER_MAX_LENGTH}
            disabled={pending}
            className="min-h-[40px] min-w-0 flex-1 resize-none bg-transparent py-2 text-base leading-6 text-foreground outline-none placeholder:text-placeholder disabled:cursor-not-allowed md:text-sm md:leading-6"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label="发送"
            title="发送 (Enter)"
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand to-primary text-primary-foreground shadow-sm transition-all",
              "hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(76,175,80,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-40",
            )}
          >
            {pending ? <Spinner className="h-4 w-4" label="发送中" /> : <ArrowUp className="h-5 w-5" strokeWidth={2.25} />}
          </button>
        </div>
        <div className="mt-1 flex min-h-[28px] items-center justify-between gap-2 px-1.5 text-caption text-muted-foreground">
          <div className="flex items-center gap-1">{actions}</div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline">Enter 发送 · Shift+Enter 换行</span>
            <span className={cn("tabular-nums", nearLimit && "font-medium text-warning-strong")}>
              {text.length}/{COMPOSER_MAX_LENGTH}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
